import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, ExpandMore, Save } from '@mui/icons-material';
import api from '../services/api';
import { formatMoney } from '../components/commercial/contact';

type Rule = { name: string; channel: string | null; paymentMethod: string | null; percent: number; fixedAmount: number; isActive: boolean };
type Summary = { units: number; revenue: number; cogs: number; grossProfit: number; channelCosts: number; commission: number; netProfit: number; netMarginPct: number; netPerUnit: number; breakdown: Record<string, number> };

// Costos típicos de la planilla del cliente, para arrancar con un clic.
const TEMPLATE: Rule[] = [
  { name: 'IIBB', channel: null, paymentMethod: null, percent: 3.5, fixedAmount: 0, isActive: true },
  { name: 'SIRCREB', channel: null, paymentMethod: null, percent: 4, fixedAmount: 0, isActive: true },
  { name: 'Débito bancario', channel: null, paymentMethod: null, percent: 0.6, fixedAmount: 0, isActive: true },
  { name: 'Crédito bancario', channel: null, paymentMethod: null, percent: 0.62, fixedAmount: 0, isActive: true },
  { name: 'Envío Nube', channel: 'Tiendanube', paymentMethod: null, percent: 6, fixedAmount: 0, isActive: true },
  { name: 'Cargo por vender ML', channel: 'MercadoLibre', paymentMethod: null, percent: 0, fixedAmount: 990, isActive: true },
];

const Kpi: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Card variant="outlined"><CardContent>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h6" fontWeight={800} sx={{ color }}>{value}</Typography>
  </CardContent></Card>
);

const SummaryRow: React.FC<{ label: string; s: Summary }> = ({ label, s }) => (
  <TableRow>
    <TableCell>{label}</TableCell>
    <TableCell align="right">{s.units}</TableCell>
    <TableCell align="right">{formatMoney(s.revenue)}</TableCell>
    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatMoney(s.cogs)}</TableCell>
    <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{formatMoney(s.channelCosts + s.commission)}</TableCell>
    <TableCell align="right"><b style={{ color: s.netProfit >= 0 ? '#16a34a' : '#dc2626' }}>{formatMoney(s.netProfit)}</b></TableCell>
    <TableCell align="right">{s.netMarginPct}%</TableCell>
    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{formatMoney(s.netPerUnit)}</TableCell>
  </TableRow>
);

/** Ganancia neta por canal y producto después de comisiones, impuestos, envío y comisión de vendedor. */
const ChannelProfitabilityPage: React.FC = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const [from, setFrom] = useState(firstDay);
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState<any>(null);
  const [rules, setRules] = useState<Rule[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [methods, setMethods] = useState<string[]>([]);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [report, cfg] = await Promise.all([api.getChannelProfitability({ from, to }), api.getChannelCosts()]);
      setData(report);
      setRules(cfg.rules.map((r: any) => ({ name: r.name, channel: r.channel, paymentMethod: r.paymentMethod, percent: r.percent, fixedAmount: r.fixedAmount, isActive: r.isActive })));
      setChannels(Array.from(new Set(['Mayorista', 'Tiendanube', 'MercadoLibre', 'POS', 'Celular', ...cfg.channels])));
      setMethods(Array.from(new Set(['Efectivo', 'Transferencia', 'Mercado Pago', 'Tarjeta', 'Cheque', ...cfg.paymentMethods])));
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.message || 'No se pudo cargar el reporte' });
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const setRule = (i: number, patch: Partial<Rule>) => setRules(r => r.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const saveRules = async () => {
    try {
      await api.setChannelCosts(rules);
      setMsg({ type: 'success', text: 'Costos guardados' });
      load();
    } catch (e: any) {
      setMsg({ type: 'error', text: e?.response?.data?.message || 'No se pudieron guardar' });
    }
  };

  const total: Summary | undefined = data?.total;

  return (
    <Box>
      <Typography variant="h5" fontWeight={800}>Ganancia neta por canal</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Facturado − costo de la mercadería − comisiones, impuestos y envío del canal − comisión del vendedor.
      </Typography>
      {msg && <Alert severity={msg.type} onClose={() => setMsg(null)} sx={{ mb: 2 }}>{msg.text}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField size="small" type="date" label="Desde" value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="Hasta" value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
      </Stack>

      {total && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}><Kpi label="Facturado" value={formatMoney(total.revenue)} /></Grid>
          <Grid item xs={6} md={3}><Kpi label="Ganancia bruta" value={formatMoney(total.grossProfit)} /></Grid>
          <Grid item xs={6} md={3}><Kpi label="Costos de vender" value={formatMoney(total.channelCosts + total.commission)} /></Grid>
          <Grid item xs={6} md={3}><Kpi label={`Ganancia neta (${total.netMarginPct}%)`} value={formatMoney(total.netProfit)} color={total.netProfit >= 0 ? 'success.main' : 'error.main'} /></Grid>
        </Grid>
      )}

      {data?.channels?.map((c: any) => (
        <Accordion key={c.channel} defaultExpanded={data.channels.length <= 2}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
              <Typography fontWeight={700} sx={{ flex: 1 }}>{c.channel}</Typography>
              <Chip label={`${c.summary.netMarginPct}% neto`} color={c.summary.netProfit >= 0 ? 'success' : 'error'} size="small" />
              <Typography fontWeight={700}>{formatMoney(c.summary.netProfit)}</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell align="right">Unid.</TableCell>
                  <TableCell align="right">Facturado</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Mercadería</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Costos venta</TableCell>
                  <TableCell align="right">Neto</TableCell>
                  <TableCell align="right">%</TableCell>
                  <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Neto / u</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {c.products.map((p: any) => <SummaryRow key={p.productId} label={p.productName} s={p.summary} />)}
                <SummaryRow label="Total canal" s={c.summary} />
              </TableBody>
            </Table>
            {Object.keys(c.summary.breakdown).length > 0 && (
              <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                {Object.entries(c.summary.breakdown).map(([k, v]) => <Chip key={k} size="small" variant="outlined" label={`${k}: ${formatMoney(v as number)}`} />)}
                {c.summary.commission > 0 && <Chip size="small" variant="outlined" label={`Comisión vendedor: ${formatMoney(c.summary.commission)}`} />}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
      {data && data.channels?.length === 0 && <Alert severity="info">No hay ventas en el período.</Alert>}

      <Card variant="outlined" sx={{ mt: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" fontWeight={700}>Costos de vender</Typography>
            <Stack direction="row" spacing={1}>
              {rules.length === 0 && <Button onClick={() => setRules(TEMPLATE)}>Cargar los de la planilla</Button>}
              <Button startIcon={<Add />} onClick={() => setRules(r => [...r, { name: '', channel: null, paymentMethod: null, percent: 0, fixedAmount: 0, isActive: true }])}>Agregar</Button>
              <Button variant="contained" startIcon={<Save />} onClick={saveRules}>Guardar</Button>
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            "Todos" = aplica a cualquier canal o medio de pago. El % es sobre el total de la venta. Los valores precargados salen de la planilla; sumá las comisiones de Tiendanube, Pago Nube y Mercado Pago con el % de tu plan.
          </Typography>
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {rules.map((r, i) => (
              <Stack key={i} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <TextField size="small" label="Concepto" value={r.name} onChange={e => setRule(i, { name: e.target.value })} sx={{ minWidth: 200 }} />
                <TextField size="small" select label="Canal" value={r.channel ?? ''} onChange={e => setRule(i, { channel: e.target.value || null })} sx={{ minWidth: 150 }}>
                  <MenuItem value="">Todos</MenuItem>
                  {channels.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
                <TextField size="small" select label="Medio de pago" value={r.paymentMethod ?? ''} onChange={e => setRule(i, { paymentMethod: e.target.value || null })} sx={{ minWidth: 150 }}>
                  <MenuItem value="">Todos</MenuItem>
                  {methods.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
                <TextField size="small" type="number" label="%" value={r.percent} onChange={e => setRule(i, { percent: Number(e.target.value) })} sx={{ width: 90 }} />
                <TextField size="small" type="number" label="Fijo $" value={r.fixedAmount} onChange={e => setRule(i, { fixedAmount: Number(e.target.value) })} sx={{ width: 110 }} />
                <Switch checked={r.isActive} onChange={e => setRule(i, { isActive: e.target.checked })} />
                <IconButton onClick={() => setRules(x => x.filter((_, idx) => idx !== i))} aria-label="Quitar"><Delete /></IconButton>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ChannelProfitabilityPage;
